import { useState } from 'react';

import { Pagination } from '@thefarmersfront/kitchen';

import { ImageViewModal, ModalSection, Table, type TypeImageItemExtractor } from '~/components/common';

import { useModal } from '~/hooks/common';
import { useGetReturnDisposalStatusHistoriesQuery } from '~/hooks/receive';
import { getReturnDisposalStatusHistoryColumns } from '~/utils';

import type { TypeReturnDisposalStatusHistoryApi } from '~/types/receive.types';

import styles from './ReceiveReturnDisposalDetailModal.module.scss';

const HISTORY_PAGE_SIZE = 5;

const imageExtractor: TypeImageItemExtractor<string> = {
  getId: (item) => item,
  getUrl: (item) => item,
};

type TypeReturnDisposalStatusHistorySectionProps = {
  inspectionId: number;
  isEnabled: boolean;
};

/**
 * [입고관리 > 회송/폐기 관리] 상세 모달 > 상태 변경 이력 섹션
 *
 * @note 전체 이력을 조회한 뒤 5행/page로 클라이언트 페이지네이션한다(API Query Parameter 없음).
 *       증빙사진(거래명세서)은 claimImageUrls를 ImageViewModal로 노출한다.
 */
const ReturnDisposalStatusHistorySection = ({
  inspectionId,
  isEnabled,
}: TypeReturnDisposalStatusHistorySectionProps) => {
  const {
    data: histories,
    isLoading,
    isError,
  } = useGetReturnDisposalStatusHistoriesQuery({ inspectionId, enabled: isEnabled });

  const [currentPage, setCurrentPage] = useState(0);
  const [selectedImages, setSelectedImages] = useState<string[] | null>(null);

  const imageModal = useModal({ onClose: () => setSelectedImages(null) });

  const totalCount = histories?.length ?? 0;
  const pagedHistories = (histories ?? []).slice(
    currentPage * HISTORY_PAGE_SIZE,
    currentPage * HISTORY_PAGE_SIZE + HISTORY_PAGE_SIZE
  );

  const columns = getReturnDisposalStatusHistoryColumns({
    startNo: totalCount - currentPage * HISTORY_PAGE_SIZE,
    onClickViewClaimImage: (claimImageUrls) => {
      setSelectedImages(claimImageUrls);
      imageModal.handleOpenModal();
    },
  });

  const handleClickPage = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setCurrentPage(Number(e.currentTarget.value) - 1);
  };

  return (
    <ModalSection title="상태 변경 이력">
      <Table<TypeReturnDisposalStatusHistoryApi>
        className={styles['history-table']}
        data={isLoading ? null : pagedHistories}
        columns={columns}
        rowHeight={40}
        height="248px"
        loading={isLoading}
        error={isError}
      />
      {totalCount > 0 && (
        <div className={styles['history-pagination']}>
          <Pagination
            currentPage={currentPage + 1}
            totalCount={totalCount}
            pageSize={HISTORY_PAGE_SIZE}
            onClick={handleClickPage}
          />
        </div>
      )}
      {imageModal.isOpen && selectedImages && (
        <ImageViewModal
          title="증빙사진"
          size="small"
          isOpen={imageModal.isOpen}
          imageList={selectedImages}
          extractor={imageExtractor}
          getAltText={(_, index) => `증빙사진 ${index + 1}`}
          onClose={imageModal.handleCloseModal}
        />
      )}
    </ModalSection>
  );
};

export default ReturnDisposalStatusHistorySection;
